import React, { useState, useEffect } from 'react';
import { IDeveloperImage } from 'utils/entities';
import { ViewPager } from 'components/view-pager';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { ControlsBox, PagerWrapper } from 'components/unit-page/galleryABTests/styled';
import { getInfiniteNextIndex, getInfinitePrevIndex } from 'utils/gallery';
import { ControlsArrow } from 'components/unit-page/galleryABTests/GalleryPager';
import { Pager } from 'components/cards/listing/Pager';
import { ImageInfo } from './ImageInfo';
import * as Styled from './styled';


interface GalleryProps {
  images: IDeveloperImage[];
  developerName: string;
}

const IMAGE_PIXEL_DENSITY = 2;

const SliderItem: React.FC<{ url: string; title: string; developerName: string }> = ({
  url,
  title,
  developerName,
}) => {
  const breakpointValue = useScreenBreakpoint();
  const constructImageURI = useImageUrlBuilder();
  const height = Styled.rangeToGalleryHeight[breakpointValue];
  const uri = constructImageURI(url, { height: height * IMAGE_PIXEL_DENSITY });

  return (
    <Styled.SliderItemContainer>
      <div>
        <img
          data-auto="developer-image"
          data-auto-url={uri}
          src={uri}
          alt={`${title ? `${title} - ` : ''}${developerName}`}
          loading="lazy"
          width="100%"
          height="100%"
        />
      </div>
    </Styled.SliderItemContainer>
  );
};

const Gallery: React.FC<GalleryProps> = ({ images, developerName }) => {
  const [ slideIndex, setSlideIndex ] = useState(0);

  const onPrevSlide = () => {
    const total = images.length;
    const nextSlideIndex = getInfinitePrevIndex(slideIndex, total);
    setSlideIndex(nextSlideIndex);
  };

  const onNextSlide = () => {
    const total = images.length;
    const nextSlideIndex = getInfiniteNextIndex(slideIndex, total);
    setSlideIndex(nextSlideIndex);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const total = images.length;
      setSlideIndex(prevState => {
        return getInfiniteNextIndex(prevState, total);
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const imageInfo = images.length ? <ImageInfo {...images[slideIndex]} /> : null;

  return (
    <div>
      <Styled.GalleryWrapper>
        <ViewPager
          align="center"
          slideIndex={slideIndex}
          onSlideIndexChange={setSlideIndex}
        >
          {images.map(({ imageUrl }, i) => {
            return (
              <SliderItem
                key={`${imageUrl}-${i}`}
                url={imageUrl}
                developerName={developerName}
                title={images.length ? images[slideIndex].title : null}
              />
            );
          })}
        </ViewPager>
        {images.length > 1 ? (
          <ShowAt from={3}>
            <ControlsBox>
              <ControlsArrow dir="prev" onClick={onPrevSlide}/>
              <ControlsArrow dir="next" onClick={onNextSlide}/>
            </ControlsBox>
          </ShowAt>
        ) : null}
        <PagerWrapper>
          <Pager
            total={images.length}
            onChangeIndex={setSlideIndex}
            index={slideIndex}
            limit={5}
          />
        </PagerWrapper>
        <ShowAt from={2}>
          {imageInfo}
        </ShowAt>
      </Styled.GalleryWrapper>
      <ShowAt at={1}>
        {imageInfo}
      </ShowAt>
    </div>
  );
};

export default Gallery;
